import React, { useEffect, useState } from "react";
// Print-only CSS
const printStyles = `
  @media print {
    body * { visibility: hidden !important; }
    #practice-print-area, #practice-print-area * { visibility: visible !important; }
    #practice-print-area { position: absolute !important; left: 0; top: 0; width: 100vw; background: white; box-shadow: none; }
    .print:hidden { display: none !important; }
  }
`;
import { useNavigate, useSearchParams } from "react-router-dom";
import { generateQuestions } from "../services/questions.ts";
import { saveScore } from "../services/scores.ts";
import { saveAnswer } from "../services/answers.ts";
import ProgressBar from "../components/ProgressBar.tsx";
import GeneratingOverlay from "../components/GeneratingOverlay.tsx";
import FeedbackIcon from "../components/FeedbackIcon.tsx";
import ScoreSummaryCard from "../components/ScoreSummaryCard.tsx";
import TopNav, { AppNavMode } from "../components/TopNav.tsx";
import SvgQuestionRenderer from "../components/SvgQuestionRenderer.tsx";
import { SVG_BUNDLE_PREFIX, parseSvgBundle } from "../utils/svgBundle.ts";


// Define the Question type if not imported from a types file
type Question = {
  question_id: string;
  question_text: string;
  options: string[];
  correct_answer: string[];
  explanation: string;
  svg_code?: string | null;
  [key: string]: any;
};

type LearnItem = {
  question_text: string;
  answer: {
    summary: string;
    explanation: string;
    example: string;
    key_points: string[];
  };
  difficulty: string;
  preparation_stage: string;
  svg_code?: string | null;
};

type PracticeQuestionItem = {
  question_text: string;
  difficulty?: string;
  preparation_stage?: string;
  svg_code?: string | null;
};

const SUBJECT_HINT_EXAMPLES = [
  "Fractions in Maths",
  "Matrix questions in NVR",
  "Similes and Idioms",
  "Verbal Reasoning with numerics",
];

const Questions: React.FC = () => {
  // Mode: 'quiz', 'learn', or 'practice'
  const [mode, setMode] = useState<'quiz' | 'learn' | 'practice'>('quiz');
  const [showSubjectHints, setShowSubjectHints] = useState(
    () => !localStorage.getItem("hasSeenSubjectHints")
  );
  const [learnItems, setLearnItems] = useState<LearnItem[]>([]);
  // Add missing state for practice questions
  const [practiceQuestions, setPracticeQuestions] = useState<PracticeQuestionItem[]>([]);

  // Handle form submission to generate questions or learn items
  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!localStorage.getItem("hasSeenSubjectHints")) {
      localStorage.setItem("hasSeenSubjectHints", "true");
      setShowSubjectHints(false);
    }
    setGenerating(true);
    setError("");
    setShowSummary(false);
    setQuestions([]);
    setSelected({});
    setSubmitted(false);
    setShowExplanation({});
    setLearnItems([]);
    setPracticeQuestions([]);
    try {
      const response = await generateQuestions(subject, difficulty, numQuestions, age, preparationStage, country, mode);
      console.log("API Response:", response);
      let data = response.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          setError("Sorry, there was a problem generating your questions. Please try again.");
          setGenerating(false);
          setLoading(false);
          return;
        }
      }
      if (mode === 'quiz') {
        if (Array.isArray(data)) {
          setQuestions(data);
          setLoading(false);
        } else if (data && data.questions && Array.isArray(data.questions)) {
          setQuestions(data.questions);
          setLoading(false);
        } else {
          setError("No questions returned from server.");
        }
      } else if (mode === 'learn') {
        if (Array.isArray(data)) {
          setLearnItems(data);
        } else if (data && data.items && Array.isArray(data.items)) {
          setLearnItems(data.items);
        } else {
          setError("No learning content returned from server.");
        }
      } else if (mode === 'practice') {
        if (Array.isArray(data)) {
          setPracticeQuestions(data);
        } else if (data && data.items && Array.isArray(data.items)) {
          setPracticeQuestions(data.items);
        } else {
          setError("No practice questions returned from server.");
        }
      }
    } catch (err: any) {
      // Check for backend ValueError with 'Malformed JSON' message
      const backendDetail = err?.response?.data?.detail || "";
      if (backendDetail.includes("Malformed JSON in _parse_json_response")) {
        setError("Sorry, there was a problem generating your questions. Please try again.");
      } else {
        setError("Failed to generate questions");
      }
    } finally {
      setGenerating(false);
      setLoading(false);
    }
  };

    // Handle answer selection
    const handleSelect = (idx: number, optionLetter: string) => {
      setSelected((prev) => ({ ...prev, [idx]: optionLetter }));
    };
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selected, setSelected] = useState<{ [idx: number]: string }>({});
  const [showExplanation, setShowExplanation] = useState<{ [idx: number]: boolean }>({});
  // Remove initial loading state; only use generating for overlay
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [country, setCountry] = useState("UK");
  const [age, setAge] = useState<number>(8);
  const [preparationStage, setPreparationStage] = useState("11+ Preparation");
  const [subject, setSubject] = useState("");
  const [difficulty, setDifficulty] = useState("medium");
  const [numQuestions, setNumQuestions] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState<{ [idx: number]: boolean }>({});
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleModeNav = (nextMode: AppNavMode) => {
    if (nextMode === 'practice-exam') {
      navigate('/practice-exam');
      return;
    }
    setMode(nextMode);
  };

  useEffect(() => {
    const requestedMode = searchParams.get('mode');
    if (requestedMode === 'quiz' || requestedMode === 'learn' || requestedMode === 'practice') {
      setMode(requestedMode);
    }
  }, [searchParams]);

  useEffect(() => {
    if (country === "UK") {
      if (preparationStage !== "school revision" && preparationStage !== "11+ Preparation"
        && preparationStage !== "ISEB Preparation" && preparationStage !== "CAT4 Preparation"
      ) setPreparationStage("school revision");
    } else {
      if (preparationStage !== "Exam Preparation") setPreparationStage("Exam Preparation");
    }
  }, [country]);

  // Use UserCreate object from localStorage if available
  const user = {
    email: localStorage.getItem("email") || "",
    password: localStorage.getItem("password") || ""
  };

  const [submitted, setSubmitted] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [learnReveal, setLearnReveal] = useState<{ [idx: number]: { summary?: boolean; explanation?: boolean; example?: boolean; key_points?: boolean } }>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    // Check all questions are answered
    if (questions.some((_, idx) => !selected[idx])) {
      setError("Please answer all questions before submitting.");
      return;
    }
    setSubmitting({});
    try {
      // Mark as submitted
      setSubmitted(true);
      // Save score to backend
      const num_correct = questions.reduce((acc, q, idx) => (selected[idx] === q.correct_answer[0] ? acc + 1 : acc), 0);
      await saveAnswer(
        questions.map((q, idx) => ({
          question_id: q.question_id,
          is_correct: selected[idx] === q.correct_answer[0]
        }))
      );
      await saveScore({
        num_questions: questions.length,
        num_correct,
        subject,
        topic: preparationStage
      });
    } catch (err: any) {
      setError("Failed to save score");
    }
  };

  const handleShowExplanation = (idx: number) => {
    setShowExplanation((prev) => ({ ...prev, [idx]: true }));
  };

  // Show Explanation button logic removed; handled by submit




  // Error state with Try Again button
  if (error) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh]">
      <div className="text-3xl font-semibold font-body text-incorrect mb-6">{error}</div>
      <button
        className="px-6 py-2 bg-primary text-white font-semibold font-body rounded-lg hover:bg-ink focus:outline-none focus:ring-2 focus:ring-accent"
        onClick={() => {
          setError("");
          setGenerating(false);
          setLoading(false);
          setQuestions([]);
          setSelected({});
          setSubmitted(false);
          setShowExplanation({});
          setLearnItems([]);
          setPracticeQuestions([]);
          // Optionally scroll to top or focus the form
        }}
      >
        Try Again
      </button>
    </div>
  );

  // Learn mode: show learn items as quiz-like cards with reveal buttons
  const handleLearnReveal = (idx: number, section: 'summary' | 'explanation' | 'example' | 'key_points') => {
    setLearnReveal(prev => ({
      ...prev,
      [idx]: {
        ...prev[idx],
        [section]: !prev[idx]?.[section]
      }
    }));
  };

  // Score summary (Quiz mode)
  const correct = questions.reduce((acc, q, idx) => (selected[idx] === q.correct_answer[0] ? acc + 1 : acc), 0);
  const total = questions.length;
  const hasGeneratedContent =
    questions.length > 0 || learnItems.length > 0 || practiceQuestions.length > 0;

  const generateButtonLabel = mode === 'learn' ? 'Generate Learn Content' : mode === 'practice' ? 'Generate Practice Questions' : 'Generate Questions';

  const labelClass = "block text-xl font-semibold font-body text-gray-500 mb-1";
  const fieldClass = "w-full rounded-lg border border-border px-3 py-2 bg-white text-ink text-xl font-body focus:outline-none focus:border-accent";

  // Main UI: always show form, then results below
  return (
    <div className="min-h-screen">
      <TopNav activeMode={mode} onSelect={handleModeNav} />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-6 items-start">
          <main className="min-w-0 order-2 lg:order-2 relative">
            {/* Loading overlay */}
            <GeneratingOverlay
              active={generating}
              title={`AI is generating your ${mode === 'quiz' ? 'questions' : 'learning content'}...`}
              numQuestions={numQuestions}
            />

            {!hasGeneratedContent && !generating && (
              <div className="bg-white rounded-lg border border-border p-12 text-center">
                <h2 className="text-5xl font-bold font-display text-ink mb-3">
                  {mode === 'learn' ? 'Learn Concepts' : mode === 'practice' ? 'Practice Questions' : 'Quiz Generator'}
                </h2>
                <p className="text-2xl font-body text-gray-600 mb-8">
                  {mode === 'learn'
                    ? 'Master key concepts with explanations and examples.'
                    : mode === 'practice'
                    ? 'Generate practice questions and print worksheets.'
                    : 'Use the form on the left to generate your questions.'}
                </p>
                <div className="grid sm:grid-cols-3 gap-4 max-w-xl mx-auto text-left">
                  <div className="rounded-lg bg-lightbg p-4">
                    <div className="text-lg font-bold font-body text-primary mb-1">1. Set up</div>
                    <div className="text-lg font-body text-gray-600">Choose subject, age and difficulty.</div>
                  </div>
                  <div className="rounded-lg bg-lightbg p-4">
                    <div className="text-lg font-bold font-body text-primary mb-1">2. Generate</div>
                    <div className="text-lg font-body text-gray-600">AI creates questions instantly.</div>
                  </div>
                  <div className="rounded-lg bg-lightbg p-4">
                    <div className="text-lg font-bold font-body text-primary mb-1">3. Learn</div>
                    <div className="text-lg font-body text-gray-600">Answer, review and track progress.</div>
                  </div>
                </div>
              </div>
            )}

            {/* Practice mode cards (Practice mode only) */}
            {mode === 'practice' && practiceQuestions.length > 0 && (
              <div id="practice-print-area" className="space-y-4">
                <style>{printStyles}</style>
                {practiceQuestions.map((q, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-lg shadow-none p-5 border border-border"
                  >
                    <div className="text-lg font-semibold font-body text-gray-500 mb-2">Q{idx + 1} of {practiceQuestions.length}</div>
                    {q.svg_code && (
                      <SvgQuestionRenderer
                        svgCode={q.svg_code}
                        questionText={q.question_text}
                        className="mb-3 mx-auto"
                      />
                    )}
                    <div className="text-3xl font-semibold font-display text-ink">{q.question_text}</div>
                  </div>
                ))}
                <div className="flex justify-center mt-8">
                  <button
                    onClick={() => window.print()}
                    className="bg-primary text-white font-semibold font-body py-2 px-8 rounded-lg hover:bg-ink transition-colors duration-200 print:hidden"
                    aria-label="Print practice questions"
                  >
                    Print
                  </button>
                </div>
              </div>
            )}

            {/* Progress Bar (Quiz mode only) */}
            {mode === 'quiz' && questions.length > 0 && (
              <ProgressBar
                current={Object.keys(selected).filter(idx => selected[Number(idx)] !== undefined).length}
                total={questions.length}
              />
            )}

            {/* Quiz questions UI (Quiz mode only) */}
            {mode === 'quiz' && questions.length > 0 && (
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  {questions.map((q, idx) => {
                    const svgBundle = parseSvgBundle(q.svg_code);
                    const questionSvgCode = svgBundle?.question || (q.svg_code && !q.svg_code.startsWith(SVG_BUNDLE_PREFIX) ? q.svg_code : null);
                    const hasAnswered = selected[idx] !== undefined;
                    const isCorrect = selected[idx] === q.correct_answer[0];
                    let borderClass = 'border border-border';
                    if (submitted && hasAnswered) {
                      borderClass = isCorrect ? 'border-2 border-success' : 'border-2 border-incorrect';
                    }
                    return (
                      <div
                        key={idx}
                        className={`bg-white rounded-lg p-5 transition-colors duration-300 relative ${borderClass}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-lg font-semibold font-body text-gray-500">Q{idx + 1} of {questions.length}</span>
                        </div>
                        <div className="flex items-center mb-4">
                          <span className="text-3xl font-semibold font-display text-ink">{q.question_text}</span>
                          {submitted && hasAnswered && (
                            <span className="ml-3">
                              <FeedbackIcon isCorrect={isCorrect} />
                            </span>
                          )}
                        </div>
                        {questionSvgCode && (
                          <SvgQuestionRenderer
                            svgCode={questionSvgCode}
                            questionText={q.question_text}
                            className="mx-auto"
                          />
                        )}
                        <div className="flex flex-col gap-2 mb-4">
                          {q.options.map((opt, oidx) => {
                            const optionLetter = String.fromCharCode(65 + oidx);
                            const optionSvgCode = svgBundle?.options?.[optionLetter] || null;
                            // Remove leading 'A) ', 'B) ', etc. from option text if present
                            const cleanedOpt = opt.replace(/^([A-D][).]\s*)/, '');
                            return (
                              <label
                                key={oidx}
                                className={`flex items-center px-4 py-2 rounded-lg cursor-pointer text-2xl font-body transition-colors duration-200 border ${
                                  selected[idx] === optionLetter
                                    ? 'bg-lightbg border-accent'
                                    : 'bg-white border-border hover:bg-lightbg'
                                } ${submitted ? 'opacity-60 cursor-not-allowed' : ''}`}
                              >
                                <input
                                  type="radio"
                                  name={`q_${idx}`}
                                  value={optionLetter}
                                  checked={selected[idx] === optionLetter}
                                  onChange={() => handleSelect(idx, optionLetter)}
                                  disabled={submitted}
                                  className="w-5 h-5 mr-3 accent-primary"
                                />
                                <span className="font-semibold mr-2 text-ink">{optionLetter}.</span>
                                {optionSvgCode ? (
                                  <SvgQuestionRenderer
                                    svgCode={optionSvgCode}
                                    questionText={`Option ${optionLetter} visual`}
                                    noMargin={true}
                                    className="mr-3 w-[120px] shrink-0"
                                  />
                                ) : (
                                  cleanedOpt
                                )}
                              </label>
                            );
                          })}
                        </div>
                        {submitted && hasAnswered && (
                          <div className="mt-4 flex flex-col items-start">
                            <div className={`text-xl font-semibold font-body flex items-center gap-2 ${isCorrect ? 'text-success' : 'text-incorrect'}`}>
                              {isCorrect ? 'Correct!' : 'Incorrect.'}
                            </div>
                            <button
                              onClick={() => handleShowExplanation(idx)}
                              className="mt-2 bg-transparent border border-primary text-primary font-semibold font-body py-1 px-4 rounded-lg hover:bg-lightbg transition-colors duration-200"
                              type="button"
                            >
                              Show Explanation
                            </button>
                            {showExplanation[idx] && (
                              <div className="mt-4 bg-lightbg border-l-4 border-accent p-4 rounded-lg text-xl font-body text-gray-600">
                                <b className="text-ink">Explanation:</b> {q.explanation}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {questions.length > 0 && !submitted && (
                  <button
                    type="submit"
                    className="mt-8 bg-primary text-white font-semibold font-body text-2xl py-3 px-8 rounded-lg hover:bg-ink transition-colors duration-200"
                  >
                    Submit All Answers
                  </button>
                )}
                {submitted && (
                  <div className="flex flex-col items-center mt-8">
                    <div className="text-3xl font-bold font-display text-ink mb-2">You scored {correct} out of {total}!</div>
                    <button
                      className="bg-primary text-white font-semibold font-body py-2 px-8 rounded-lg hover:bg-ink transition-colors duration-200"
                      onClick={() => window.location.reload()}
                      type="button"
                    >
                      Try Another Quiz
                    </button>
                    <a
                      href="/dashboard"
                      className="mt-4 text-primary font-body underline hover:text-ink"
                    >
                      View Score Dashboard
                    </a>
                  </div>
                )}
              </form>
            )}

            {/* Learn mode cards (Learn mode only) */}
            {mode === 'learn' && learnItems.length > 0 && (
              <div className="space-y-4">
                {learnItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-lg p-5 border border-border relative"
                  >
                    <div className="text-lg font-semibold font-body text-gray-500 mb-1">Q{idx + 1} of {learnItems.length}</div>
                    <div className="flex items-center mb-4">
                      <span className="text-3xl font-semibold font-display text-ink">{item.question_text}</span>
                    </div>
                    {item.svg_code && (
                      <SvgQuestionRenderer
                        svgCode={item.svg_code}
                        questionText={item.question_text}
                        className="mx-auto"
                      />
                    )}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <button
                        className="bg-transparent border border-primary text-primary font-semibold font-body py-1 px-4 rounded-lg hover:bg-lightbg transition-colors duration-200"
                        onClick={() => handleLearnReveal(idx, 'summary')}
                        type="button"
                      >
                        {learnReveal[idx]?.summary ? 'Hide Summary' : 'Show Summary'}
                      </button>
                      <button
                        className="bg-transparent border border-primary text-primary font-semibold font-body py-1 px-4 rounded-lg hover:bg-lightbg transition-colors duration-200"
                        onClick={() => handleLearnReveal(idx, 'explanation')}
                        type="button"
                      >
                        {learnReveal[idx]?.explanation ? 'Hide Explanation' : 'Show Explanation'}
                      </button>
                      <button
                        className="bg-transparent border border-primary text-primary font-semibold font-body py-1 px-4 rounded-lg hover:bg-lightbg transition-colors duration-200"
                        onClick={() => handleLearnReveal(idx, 'example')}
                        type="button"
                      >
                        {learnReveal[idx]?.example ? 'Hide Example' : 'Show Example'}
                      </button>
                      <button
                        className="bg-transparent border border-primary text-primary font-semibold font-body py-1 px-4 rounded-lg hover:bg-lightbg transition-colors duration-200"
                        onClick={() => handleLearnReveal(idx, 'key_points')}
                        type="button"
                      >
                        {learnReveal[idx]?.key_points ? 'Hide Key Points' : 'Show Key Points'}
                      </button>
                    </div>
                    {learnReveal[idx]?.summary && (
                      <div className="mb-2 text-xl font-body text-gray-600"><span className="font-semibold text-ink">Summary:</span> {item.answer.summary}</div>
                    )}
                    {learnReveal[idx]?.explanation && (
                      <div className="mb-2 text-xl font-body text-gray-600"><span className="font-semibold text-ink">Explanation:</span> {item.answer.explanation}</div>
                    )}
                    {learnReveal[idx]?.example && (
                      <div className="mb-2 text-xl font-body text-gray-600"><span className="font-semibold text-ink">Example:</span> {item.answer.example}</div>
                    )}
                    {learnReveal[idx]?.key_points && (
                      <div className="mb-2 text-xl font-body text-gray-600">
                        <span className="font-semibold text-ink">Key Points:</span>
                        <ul className="list-disc ml-6 mt-1">
                          {item.answer.key_points.map((point, i) => (
                            <li key={i}>{point}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </main>

          <aside className="order-1 lg:order-1 lg:sticky lg:top-20">
            <div className="bg-white rounded-lg border border-border p-5">
              <h3 className="text-2xl font-semibold font-display text-ink mb-3">{generateButtonLabel}</h3>
              <form onSubmit={handleGenerate} className="flex flex-col gap-3">
                <div>
                  <label className={labelClass}>Subject</label>
                  <input
                    className={fieldClass}
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    required
                    placeholder="e.g. Maths"
                  />
                  {showSubjectHints && (
                    <div className="mt-2">
                      <div className="text-sm font-body text-gray-500 mb-1">Try:</div>
                      <div className="flex flex-wrap gap-1.5">
                        {SUBJECT_HINT_EXAMPLES.map((example) => (
                          <button
                            key={example}
                            type="button"
                            onClick={() => setSubject(example)}
                            className="text-sm font-body px-2 py-1 rounded-md bg-lightbg border border-border text-gray-600 hover:bg-white hover:border-accent transition-colors duration-200"
                          >
                            {example}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className={labelClass}>Country</label>
                  <select
                    className={fieldClass}
                    value={country}
                    onChange={e => setCountry(e.target.value)}
                  >
                    <option value="UK">UK</option>
                    <option value="India">India</option>
                    <option value="Sweden">Sweden</option>
                    <option value="US">US</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Age</label>
                  <select
                    className={fieldClass}
                    value={age}
                    onChange={e => setAge(Number(e.target.value))}
                    required
                  >
                    {Array.from({ length: 15 }, (_, i) => 4 + i).map(val => (
                      <option key={val} value={val}>{val}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Number of Questions</label>
                  <select
                    className={fieldClass}
                    value={numQuestions}
                    onChange={e => setNumQuestions(Number(e.target.value))}
                  >
                    {Array.from({ length: 10 }, (_, i) => i + 1).map(val => (
                      <option key={val} value={val}>{val}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Difficulty</label>
                  <select
                    className={fieldClass}
                    value={difficulty}
                    onChange={e => setDifficulty(e.target.value)}
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>What are you preparing for?</label>
                  {country === "UK" ? (
                    <select
                      className={fieldClass}
                      value={preparationStage}
                      onChange={e => setPreparationStage(e.target.value)}
                    >
                      <option value="school revision">School Revision</option>
                      <option value="11+ Preparation">11+ Preparation</option>
                      <option value="ISEB Preparation">ISEB Preparation</option>
                      <option value="CAT4 Preparation">CAT4 Preparation</option>
                    </select>
                  ) : (
                    <select
                      className={fieldClass}
                      value={preparationStage}
                      onChange={e => setPreparationStage(e.target.value)}
                    >
                      <option value="Exam Preparation">Exam Preparation</option>
                    </select>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={generating}
                  className="w-full bg-primary text-white font-semibold font-body text-2xl py-2 px-4 rounded-lg hover:bg-ink transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generating ? "Generating..." : generateButtonLabel}
                </button>
              </form>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default Questions;
